import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import {
  getPassengerCities,
  createPassengerCity,
  updatePassengerCity,
  deletePassengerCity,
} from "../api/client";
import { useWorkspaceSegment } from "../hooks/useWorkspaceSegment";
import { WorkspaceSegments } from "../constants/workspaceSegments";
import {
  can,
  useEffectivePermissions,
  effectiveScopeSetForUser,
} from "../hooks/useEffectivePermissions";
import { useAuthContext } from "../context/AuthContext";
import { ensureNamespaceLoaded } from "../i18n/lazyNamespaces";
import PassengerCitiesTable from "../components/PassengerCitiesTable";

const emptyForm = () => ({
  nome: "",
  nomecurto: "",
  id_interno: "",
  uf: "",
  fonte: "manual",
  status: 1,
});

const PassengerCitiesPage = () => {
  const { t, i18n } = useTranslation("products");
  const { segment } = useWorkspaceSegment();
  const { userData } = useAuthContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: permData } = useEffectivePermissions();
  const scopeSet = useMemo(
    () =>
      effectiveScopeSetForUser(permData?.scopes, {
        isMaster: Number(userData?.master) === 1,
      }),
    [permData?.scopes, userData?.master]
  );

  const canCreate = can(scopeSet, "cities", "create");
  const canUpdate = can(scopeSet, "cities", "update");
  const canDelete = can(scopeSet, "cities", "delete");

  const [i18nReady, setI18nReady] = useState(() =>
    i18n.hasResourceBundle(i18n.resolvedLanguage || i18n.language || "pt-BR", "products")
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void ensureNamespaceLoaded("products").then(() => {
      if (!cancelled) setI18nReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const tmr = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(tmr);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await getPassengerCities({
        page: page + 1,
        limit: rowsPerPage,
        q: debounced,
      });
      const d =
        raw && Array.isArray(raw.items)
          ? raw
          : raw?.data && typeof raw.data === "object"
            ? raw.data
            : {};
      setItems(Array.isArray(d?.items) ? d.items : []);
      setTotal(Number(d?.total) || 0);
    } catch (e) {
      setError(e?.message || t("passengerCitiesPage.loadError"));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debounced, t]);

  useEffect(() => {
    load();
  }, [load]);

  if (segment !== WorkspaceSegments.PassengerTransport) {
    return <Navigate to="/products" replace />;
  }

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      nome: row.nome || "",
      nomecurto: row.nomecurto || "",
      id_interno: row.id_interno || "",
      uf: row.uf || "",
      fonte: row.fonte || "manual",
      status: Number(row.status) === 0 ? 0 : 1,
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const nome = String(form.nome || "").trim();
    const id_interno = String(form.id_interno || "").trim();
    if (!nome) {
      setFormError(t("passengerCitiesPage.errorNome"));
      return;
    }

    setSaving(true);
    setFormError(null);
    const body = {
      nome,
      nomecurto: String(form.nomecurto || "").trim() || nome,
      id_interno: id_interno || null,
      uf: String(form.uf || "").trim().toUpperCase(),
      fonte: String(form.fonte || "").trim() || "manual",
      status: Number(form.status) === 0 ? 0 : 1,
    };

    try {
      if (editingId) {
        await updatePassengerCity(editingId, body);
      } else {
        await createPassengerCity(body);
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      setFormError(e?.message || t("passengerCitiesPage.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    if (!canUpdate) return;
    setIsProcessing(true);
    setError(null);
    try {
      await updatePassengerCity(row.id, {
        status: Number(row.status) === 1 ? 0 : 1,
      });
      await load();
    } catch (e) {
      setError(e?.message || t("passengerCitiesPage.saveError"));
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmDeleteCity = async () => {
    if (!confirmDelete) return;
    setIsProcessing(true);
    setError(null);
    try {
      await deletePassengerCity(confirmDelete.id);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      setError(e?.message || t("passengerCitiesPage.deleteError"));
    } finally {
      setIsProcessing(false);
    }
  };

  const bulkDelete = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    setError(null);
    try {
      await Promise.all(ids.map((id) => deletePassengerCity(id)));
      await load();
    } catch (e) {
      setError(e?.message || t("passengerCitiesPage.deleteError"));
    } finally {
      setIsProcessing(false);
    }
  };

  const titleReady =
    i18nReady || !String(t("passengerCitiesPage.title")).startsWith("passengerCitiesPage.");

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {titleReady ? t("passengerCitiesPage.title") : "…"}
        </Typography>
        {canCreate ? (
          <Button
            variant="contained"
            color="primary"
            fullWidth={isMobile}
            onClick={openCreate}
          >
            {t("passengerCitiesPage.add")}
          </Button>
        ) : null}
      </Box>

      {error ? (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Box>
      ) : null}

      <PassengerCitiesTable
        items={items}
        rowCount={total}
        page={page}
        pageSize={rowsPerPage}
        onPageChange={setPage}
        onPageSizeChange={(sz) => {
          setRowsPerPage(sz);
          setPage(0);
        }}
        searchValue={search}
        onSearchChange={(term) => {
          setSearch(term);
          setPage(0);
        }}
        onSearchSubmit={(term) => {
          setDebounced(String(term || "").trim());
          setPage(0);
        }}
        isLoading={loading || isProcessing}
        canUpdate={canUpdate}
        canDelete={canDelete}
        onEdit={openEdit}
        onDelete={(row) => setConfirmDelete(row)}
        onToggleStatus={toggleStatus}
        onBulkDelete={bulkDelete}
      />

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? t("passengerCitiesPage.editTitle") : t("passengerCitiesPage.createTitle")}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {formError ? (
            <Alert severity="error" onClose={() => setFormError(null)}>
              {formError}
            </Alert>
          ) : null}
          <TextField
            label={t("passengerCitiesPage.fieldNome")}
            value={form.nome}
            onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))}
            required
            fullWidth
            autoFocus
            margin="dense"
          />
          <TextField
            label={t("passengerCitiesPage.fieldNomeCurto")}
            value={form.nomecurto}
            onChange={(e) => setForm((p) => ({ ...p, nomecurto: e.target.value }))}
            fullWidth
            helperText={t("passengerCitiesPage.fieldNomeCurtoHint")}
          />
          <TextField
            label={t("passengerCitiesPage.fieldCodigo")}
            value={form.id_interno}
            onChange={(e) => setForm((p) => ({ ...p, id_interno: e.target.value }))}
            fullWidth
            helperText={t("passengerCitiesPage.fieldCodigoHint")}
          />
          <TextField
            label={t("passengerCitiesPage.fieldUf")}
            value={form.uf}
            onChange={(e) => setForm((p) => ({ ...p, uf: e.target.value }))}
            fullWidth
            inputProps={{ maxLength: 8 }}
          />
          <TextField
            label={t("passengerCitiesPage.fieldFonte")}
            value={form.fonte}
            onChange={(e) => setForm((p) => ({ ...p, fonte: e.target.value }))}
            fullWidth
            helperText={t("passengerCitiesPage.fieldFonteHint")}
          />
          <TextField
            select
            label={t("passengerCitiesPage.fieldStatus")}
            value={form.status}
            onChange={(e) => setForm((p) => ({ ...p, status: Number(e.target.value) }))}
            fullWidth
          >
            <MenuItem value={1}>{t("table.statusActive")}</MenuItem>
            <MenuItem value={0}>{t("table.statusInactive")}</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            {t("passengerCitiesPage.cancel")}
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : t("passengerCitiesPage.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => !isProcessing && setConfirmDelete(null)}>
        <DialogTitle>{t("passengerCitiesPage.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing
              ? t("passengerCitiesPage.deleting")
              : t("passengerCitiesPage.confirmDelete", {
                  name: confirmDelete?.nome || confirmDelete?.id || "",
                })}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>
                {t("passengerCitiesPage.cancel")}
              </Button>
              <Button color="error" onClick={confirmDeleteCity}>
                {t("passengerCitiesPage.delete")}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PassengerCitiesPage;
